import { useState, useMemo } from "react";
import { IoCloseCircle } from "react-icons/io5";
import { FaCcAmazonPay } from "react-icons/fa";
import { CiBullhorn } from "react-icons/ci";
import { Row, Col, Modal } from "react-bootstrap";
import ExternalProductLink from "../merchandise/ExternalProductLink";
import { trellisPalette } from "../analytics/palettes";
import { Button } from "../../core/basic/Button";
import BuyBoxOffers from "../../../modules/pricingv2/cards/BuyBoxOffers";
import { Link } from "@tanstack/react-router";
import { ButtonGroup } from "../../core/basic/ButtonGroup";
import { Center } from "../../../../styled-system/jsx";
import { cut } from "../../../utils/formatText";
import { css } from "../../../../styled-system/css";
import { useTranslation } from "react-i18next";

const BudgetStatusNotification = ({ notification }) => {
  const { t } = useTranslation(["common"]);
  return (
    <div className="d-flex justify-content-between align-items-center py-3" style={{ borderBottom: "1px solid #ddd" }}>
      <div style={{ border: "1px solid #623f7f", padding: "1rem 1.5rem 1rem 1.5rem", borderRadius: "50%" }}>
        <CiBullhorn />
      </div>
      <Row className="fs-5 ms-4">
        <Col xs={12} className="fs-4 fw-bold">
          {notification.title}
        </Col>
        <Col xs={12} className="fs-5 py-2">
          {notification.message}
        </Col>
        <Col xs={4}>
          <Link
            to="/user/advertising/dashboard/ads/$id"
            params={{ id: String(notification.meta.ad_id) }}
            target="_blank"
            style={{ cursor: "pointer", color: trellisPalette[0], textDecoration: "none" }}
          >
            {t(($) => $.common.notifications["View Media Plan"])}
          </Link>
        </Col>
        <Col xs={8} className="text-end">
          {/* Spent {formatCurrency(notification.meta.as, marketPlace.marketPlace)} on a budget of{" "}
          {formatCurrency(notification.meta.db, marketPlace.marketPlace)} */}
        </Col>
      </Row>
    </div>
  );
};

const PricingBSRNotification = ({ notification }) => {
  const { t } = useTranslation(["common"]);
  return (
    <div className="d-flex justify-content-between py-3">
      <div
        style={{
          border: "1px solid #623f7f",
          padding: "1rem 1.5rem 1rem 1.5rem",
          borderRadius: "50%",
          width: "45px",
          height: "45px",
        }}
      >
        <i className="pe-7s-cash" style={{ marginLeft: "-1px", marginTop: "3px" }} />
      </div>
      <Row className="fs-5 ms-4">
        <Col xs={12} className="fs-4 fw-bold">
          {notification.title}
        </Col>
        <Col xs={12} className="fs-5 py-2">
          {notification.message}
        </Col>
        <Col xs={4}>
          <Link
            to="/user/pricing/plan/$plan"
            params={{ plan: String(notification.meta.pid) }}
            target="_blank"
            style={{ cursor: "pointer", color: trellisPalette[0], textDecoration: "none" }}
          >
            {t(($) => $.common.notifications["View Pricing Plan"])}
          </Link>
        </Col>
        <Col xs={4} className="text-center">
          &nbsp;
        </Col>
        <Col xs={4} className="text-end">
          &nbsp;
        </Col>
      </Row>
    </div>
  );
};

const BuyBoxNotification = ({ notification, marketPlace }) => {
  const { t } = useTranslation(["common"]);
  const [selectedASIN, setSelectedASIN] = useState("");
  const [viewOffers, setViewOffers] = useState(false);

  if (notification.meta.fulfillment_channel === "MFN") {
    return null;
  }

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center py-3"
        style={{ borderBottom: "1px solid #ddd" }}
      >
        <div style={{ border: "1px solid #623f7f", padding: "1rem 1.5rem 1rem 1.5rem", borderRadius: "50%" }}>
          <FaCcAmazonPay />
        </div>
        <Row className="fs-5 ms-4">
          <Col xs={12} className="fs-4 fw-bold">
            {notification.title}
          </Col>
          <Col xs={12} className="fs-5 py-2">
            {notification.message}
          </Col>
          <Col xs={4}>
            <span className="pe-3">{notification.meta.asin}</span>
            <ExternalProductLink asin={notification.meta.asin} />
          </Col>
          <Col xs={4} className="text-center">
            <div
              onClick={() => {
                setSelectedASIN(notification.meta.asin);
                setViewOffers(true);
              }}
              style={{ cursor: "pointer", color: trellisPalette[0] }}
            >
              {t(($) => $.common.notifications["View Offers"])}
            </div>
          </Col>
          <Col xs={4} className="text-end">
            {notification.meta.fulfillment_channel === "AFN" ?
              t(($) => $.common.notifications["Fulfilled by Amazon"])
            : t(($) => $.common.notifications["Fulfilled by Manufacturer"])}
          </Col>
        </Row>
      </div>
      <Modal
        show={viewOffers}
        onHide={() => {
          setViewOffers(false);
        }}
        animation={false}
        size={"xl"}
      >
        <Modal.Body>
          <BuyBoxOffers marketPlace={marketPlace} asin={selectedASIN} />
        </Modal.Body>
        <Modal.Footer>
          <Button
            className={css({ margin: "1rem" })}
            onPress={() => {
              setViewOffers(false);
            }}
          >
            {t(($) => $.common.actions["Close"])}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const NoNotifications = () => {
  const { t } = useTranslation(["common"]);
  return (
    <div className="notification px-2 mt-4">
      <div className="d-flex justify-content-between py-3">
        <div
          style={{
            border: "1px solid #623f7f",
            padding: "1rem 1.5rem 1rem 1.5rem",
            borderRadius: "50%",
            width: "45px",
            height: "45px",
          }}
        >
          <i className="fa fa-bell" style={{ marginLeft: "-2px" }} />
        </div>
        <Row className="fs-5 ms-4">
          <Col xs={12} className="fs-4 fw-bold">
            {t(($) => $.common.notifications["No Notifications"])}
          </Col>
          <Col xs={12} className="fs-5 py-2">
            {t(($) => $.common.notifications["You have no new notifications"])}
          </Col>
          <Col xs={4} className="text-center">
            &nbsp;
          </Col>
          <Col xs={4} className="text-center">
            &nbsp;
          </Col>
          <Col xs={4} className="text-center">
            &nbsp;
          </Col>
        </Row>
      </div>
    </div>
  );
};

const AdUpdated = ({ notification }) => {
  const { t } = useTranslation(["common"]);
  return (
    <div className="d-flex justify-content-between align-items-center py-3" style={{ borderBottom: "1px solid #ddd" }}>
      <div style={{ border: "1px solid #623f7f", padding: "1rem 1.5rem 1rem 1.5rem", borderRadius: "50%" }}>
        <CiBullhorn />
      </div>
      <Row className="fs-5 ms-4">
        <Col xs={12} className="fs-4 fw-bold">
          {notification.title}
        </Col>
        <Col xs={12} className="fs-5 py-2">
          {notification.message}
        </Col>
        <Col xs={4}>
          <Link
            to="/user/advertising/dashboard/ads/$id"
            params={{ id: String(notification.meta.ad_id) }}
            target="_blank"
            style={{ cursor: "pointer", color: trellisPalette[0], textDecoration: "none" }}
          >
            {t(($) => $.common.notifications["View Media Plan"])}
          </Link>
        </Col>
        <Col xs={4} className="text-center">
          &nbsp;
        </Col>
        <Col xs={4} className="text-end">
          &nbsp;
        </Col>
      </Row>
    </div>
  );
};

const PromoStartedEnded = ({ notification }) => {
  const { t } = useTranslation(["common"]);
  return (
    <div className="d-flex justify-content-between align-items-center py-3" style={{ borderBottom: "1px solid #ddd" }}>
      <div style={{ border: "1px solid #623f7f", padding: "1rem 1.5rem 1rem 1.5rem", borderRadius: "50%" }}>
        <FaCcAmazonPay />
      </div>
      <Row className="fs-5 ms-4">
        <Col xs={12} className="fs-4 fw-bold">
          {notification.title}
        </Col>
        <Col xs={12} className="fs-5 py-2">
          {notification.message} {t(($) => $.common.notifications["on product"])}{" "}
          <Link
            to="/user/merchandising/product/$product"
            params={{ product: String(notification.meta.product_id) }}
            target="_blank"
            style={{ cursor: "pointer", color: trellisPalette[0], textDecoration: "none" }}
          >
            <span className="pe-3">{cut(notification.meta.product_title, 75)}</span>
          </Link>
          <ExternalProductLink asin={notification.meta.product_id} />
        </Col>
      </Row>
    </div>
  );
};

const CompetitivePriceNotification = ({ notification }) => {
  const { t } = useTranslation(["common"]);
  return (
    <div className="d-flex justify-content-between align-items-center py-3" style={{ borderBottom: "1px solid #ddd" }}>
      <div style={{ border: "1px solid #623f7f", padding: "1rem 1.5rem 1rem 1.5rem", borderRadius: "50%" }}>
        <FaCcAmazonPay />
      </div>
      <Row className="fs-5 ms-4">
        <Col xs={12} className="fs-4 fw-bold">
          {notification.title}
        </Col>
        <Col xs={12} className="fs-5 py-2">
          {notification.message}
        </Col>
        <Col xs={4}>
          <Link
            to="/user/merchandising/product/$product"
            params={{ product: String(notification.meta.product_id) }}
            target="_blank"
            style={{ cursor: "pointer", color: trellisPalette[0], textDecoration: "none" }}
          >
            {t(($) => $.common.notifications["View Product"])}
          </Link>
          &nbsp;
          <ExternalProductLink asin={notification.meta.asin} />
        </Col>
        <Col xs={4} className="text-center">
          &nbsp;
        </Col>
        <Col xs={4} className="text-end">
          &nbsp;
        </Col>
      </Row>
    </div>
  );
};

const NotificationDropdown = ({ showNotifications, setShowNotifications, notifications, isLoading, marketPlace }) => {
  const { t } = useTranslation(["common"]);
  const [notificationView, setNotificationView] = useState("all");

  const filteredNotifications = useMemo(() => {
    if (notificationView === "all") {
      return notifications;
    }

    const filtered = [];
    for (let i = 0; i < notifications.length; i++) {
      const n = notifications[i];
      let type = n.type;
      if (type === "cmp") {
        type = "pricing";
      } else if (type === "AD_UPDATED" || type === "AD_CREATED") {
        type = "other";
      } else if (type === "PROMO_STARTED" || type === "PROMO_ENDED") {
        type = "other";
      }
      if (type === notificationView) {
        filtered.push(n);
      }
    }

    return filtered;

    // return notifications.filter((n) => type === notificationView);
  }, [notifications, notificationView]);

  const notificationTypeCount = useMemo(() => {
    return notifications.reduce((acc, n) => {
      let type = n.type;
      if (type === "cmp") {
        type = "pricing";
      } else if (type === "AD_UPDATED" || type === "AD_CREATED") {
        type = "other";
      } else if (type === "PROMO_STARTED" || type === "PROMO_ENDED") {
        type = "other";
      }
      if (acc[type]) {
        acc[type] += 1;
      } else {
        acc[type] = 1;
      }
      return acc;
    }, {});
  }, [notifications]);

  return (
    <div
      className="notification-panel"
      style={{
        transform: showNotifications ? "translateX(0)" : "translateX(100%)",
        transition: "transform 10.5s ease",
        width: "550px",
        position: "fixed",
        right: "0",
        top: "0",
        height: "100vh",
        backgroundColor: "#f8f9fa",
        boxShadow: "0 0 5px rgba(0,0,0,0.5)",
        zIndex: "1059",
        overflowY: "auto",
      }}
    >
      <div
        className="d-flex justify-content-between align-items-center px-3 pt-4 pb-2 mb-3"
        style={{ borderBottom: "1px solid #ccc" }}
      >
        <div className={`fs-3 fw-bold`}>{t(($) => $.common.notifications["Notifications"])}</div>
        <div
          className={`fs-3 fw-bold`}
          onClick={() => {
            setShowNotifications(false);
          }}
          style={{
            cursor: "pointer",
          }}
        >
          <IoCloseCircle />
        </div>
      </div>

      {isLoading && <div className="text-center">{t(($) => $.common["Loading..."])}</div>}
      {!isLoading && (
        <div className="px-3 me-4">
          <Center>
            <ButtonGroup
              buttons={[
                {
                  value: "all",
                  label: t(($) => $.common.notifications["All"]),
                  counter: notifications.length,
                },
                {
                  value: "bgt",
                  label: t(($) => $.common.notifications["Budget"]),
                  counter: notificationTypeCount["bgt"] ? notificationTypeCount["bgt"] : 0,
                },
                {
                  value: "pricing",
                  label: t(($) => $.common.notifications["Price"]),
                  counter: notificationTypeCount["pricing"] ? notificationTypeCount["pricing"] : 0,
                },
                {
                  value: "bb",
                  label: t(($) => $.common.notifications["Buy Box"]),
                  counter: notificationTypeCount["bb"] ? notificationTypeCount["bb"] : 0,
                },
              ]}
              value={notificationView}
              onSelect={setNotificationView}
            />
          </Center>

          {filteredNotifications && filteredNotifications.length === 0 && <NoNotifications />}
          {filteredNotifications.map((noti) => (
            <div key={noti.id} className="notification px-2 mt-4">
              {noti.type === "bb" && <BuyBoxNotification notification={noti} marketPlace={marketPlace} />}
              {noti.type === "pricing" && <PricingBSRNotification notification={noti} />}
              {noti.type === "bgt" && <BudgetStatusNotification notification={noti} />}
              {noti.type === "AD_UPDATED" && <AdUpdated notification={noti} />}
              {noti.type === "AD_CREATED" && <AdUpdated notification={noti} />}
              {noti.type === "PROMO_STARTED" && <PromoStartedEnded notification={noti} />}
              {noti.type === "PROMO_ENDED" && <PromoStartedEnded notification={noti} />}
              {noti.type === "cmp" && <CompetitivePriceNotification notification={noti} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
